import * as React from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { Text, MonoText } from "./typography";
import { CurrencyValue } from "./value-display";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteDividendDialog - Confirmation dialog for deleting a dividend
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when dividend is deleted successfully
 * @param {Object} props.dividend - The dividend to delete
 */
export function DeleteDividendDialog({
  open,
  onOpenChange,
  onSuccess,
  dividend,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!dividend?.transactionId) {
      setError("No dividend selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/transactions/dividends/${dividend.transactionId}`,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete dividend:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!dividend) {
    return null;
  }

  const isAnnual = dividend.statementType === "ANNUAL";
  const totalAmount = isAnnual
    ? dividend.totalAmount
    : (dividend.dividendPerShare || 0) * (dividend.sharesOwned || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Dividend
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete this
            dividend record.
          </DialogDescription>
        </DialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        {/* Dividend Details */}
        <div className="rounded-lg border border-border/50 p-4 space-y-2">
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Type</Text>
            <Text size="sm" className="font-medium">
              {isAnnual ? "Annual Statement" : "Individual Dividend"}
            </Text>
          </div>
          {!isAnnual && (
            <>
              <div className="flex justify-between items-center">
                <Text variant="secondary" size="sm">Asset</Text>
                <Text size="sm" className="font-medium">
                  {dividend.assetName || dividend.ticker}
                </Text>
              </div>
              {dividend.ticker && dividend.ticker !== dividend.assetName && (
                <div className="flex justify-between items-center">
                  <Text variant="secondary" size="sm">Ticker</Text>
                  <MonoText size="sm">{dividend.ticker}</MonoText>
                </div>
              )}
            </>
          )}
          {isAnnual && dividend.broker && (
            <div className="flex justify-between items-center">
              <Text variant="secondary" size="sm">Broker</Text>
              <Text size="sm" className="font-medium">{dividend.broker}</Text>
            </div>
          )}
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Amount</Text>
            <CurrencyValue
              value={totalAmount}
              currency={dividend.currency}
              size="sm"
              className="font-medium"
            />
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Date</Text>
            <MonoText size="sm">{dividend.date || dividend.paymentDate}</MonoText>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteDividendDialog;
